<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Empty State Card Component
      p.text--secondary A specialized card component designed to communicate empty states, no data scenarios, and guide users toward meaningful actions.

    // Basic Empty State Examples
    .component-section.mb-8#basic-empty-states
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-empty-states')") Basic Empty State Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in basicVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Empty State Card Implementation
            yz-empty-state-card(v-bind="variation.props" style="max-width: 400px;" @action-click="handleActionClick(variation.name)")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Props Examples
    .component-section.mb-8#props-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('props-examples')") Props Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in propsVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Empty State Card Implementation
            yz-empty-state-card(v-bind="variation.props" style="max-width: 400px;" @action-click="handleActionClick(variation.name)")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Slot Examples
    .component-section.mb-8#slot-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('slot-examples')") Slot Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in slotVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Empty State Card Implementation with Slots
            yz-empty-state-card(v-bind="variation.props" style="max-width: 400px;" @action-click="handleActionClick(variation.name)")
              template(v-if="variation.slotType === 'icon'" v-slot:icon)
                .d-flex.align-center
                  yz-icon(size="24" color="yz-neutral-500") $yzi-star-01
                  yz-icon(size="24" color="yz-success-500") $yzi-check-circle
                  yz-icon(size="24" color="yz-danger-500") $yzi-alert-circle
              template(v-if="variation.slotType === 'title'" v-slot:title)
                .mb-4.text-center
                  .yz-bold-body-sm.text-primary-500 Premium Feature
                  .yz-regular-body-xs.text-neutral-400 Upgrade Required
              template(v-if="variation.slotType === 'action'" v-slot:action)
                .d-flex.gap-2
                  yz-btn(outlined size="sm" label="Browse Files")
                  yz-btn(color="primary" size="sm" label="Drag & Drop")

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      
      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in componentProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}
      
      h4.mb-3 Slots
      .slots-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot
                th Description
            tbody
              tr(v-for="slot in componentSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}

      h4.mb-3 Events
      .events-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Parameters
                th Description
            tbody
              tr(v-for="event in componentEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.text--secondary.text-no-wrap {{ event.parameters }}
                td.single-line {{ event.description }}
      
      h4.mb-3 Common Use Cases
      .use-cases-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Scenario
                th Recommended Approach
                th Example Message
            tbody
              tr(v-for="useCase in useCases" :key="useCase.scenario")
                td.font-weight-medium.text-no-wrap {{ useCase.scenario }}
                td.text--secondary {{ useCase.approach }}
                td.single-line {{ useCase.example }}

      h4.mb-3 Implementation Notes
      .notes-section
        v-alert(type="info" outlined)
          ul.mb-0
            li
              strong Layout: 
              | Empty state cards should be centered with adequate padding (typically 24px or more)
            li
              strong Icons: 
              | Use 48px or larger icons with neutral colors to avoid drawing too much attention
            li
              strong Actions: 
              | Limit to 1-2 primary actions to avoid overwhelming the user
            li
              strong Text: 
              | Keep titles concise (1-5 words) and descriptions helpful but brief
            li
              strong Consistency: 
              | Use consistent spacing, colors, and tone across all empty states
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null
    }
  },
  computed: {
    basicVariations() {
      return [
        {
          name: 'Simple Empty State',
          id: 'simple-empty-state',
          description: 'Basic empty state with icon, title, and description',
          label: 'Simple Empty State',
          hint: 'Minimal approach',
          props: {
            icon: '$yzi-users-02',
            title: 'No items found',
            description: 'There are no items to display at the moment.'
          },
          code: `yz-empty-state-card(
  icon="$yzi-users-02"
  title="No items found"
  description="There are no items to display at the moment."
)`
        },
        {
          name: 'With Action Button',
          id: 'with-action-button',
          description: 'Empty state with a primary action button',
          label: 'With Action Button',
          hint: 'Includes call-to-action',
          props: {
            icon: '$yzi-plus-circle',
            title: 'Create your first project',
            description: 'Get started by creating a new project to organize your work.',
            actionLabel: 'Create Project',
          },
          code: `yz-empty-state-card(
  icon="$yzi-plus-circle"
  title="Create your first project"
  description="Get started by creating a new project to organize your work."
  action-label="Create Project"
  @action-click="handleCreate"
)`
        },
        {
          name: 'Custom Icon Size',
          id: 'custom-icon-size',
          description: 'Empty state with larger icon size',
          label: 'Custom Icon Size',
          hint: 'Large 48px icon',
          props: {
            icon: '$yzi-upload-01',
            iconSize: '48px',
            title: 'No files uploaded',
            description: 'Upload files to get started or import from your computer.',
            actionLabel: 'Upload Files',
            actionColor: 'primary'
          },
          code: `yz-empty-state-card(
  icon="$yzi-upload-01"
  icon-size="48px"
  title="No files uploaded"
  description="Upload files to get started or import from your computer."
  action-label="Upload Files"
  action-color="primary"
  @action-click="handleUpload"
)`
        }
      ]
    },
    propsVariations() {
      return [
        {
          name: 'Icon Color',
          id: 'icon-color-example',
          description: 'Customizing icon color with design tokens',
          label: 'Icon Color',
          hint: 'icon-color="yz-danger-500"',
          props: {
            icon: '$yzi-star-01',
            iconColor: 'yz-danger-500',
            title: 'No favorites yet',
            description: 'Items you mark as favorites will appear here.'
          },
          code: `yz-empty-state-card(
  icon="$yzi-star-01"
  icon-color="yz-primary-500"
  title="No favorites yet"
  description="Items you mark as favorites will appear here."
)`
        },
        {
          name: 'Border Style',
          id: 'border-style-example',
          description: 'Different border styles for the card',
          label: 'Border Style',
          hint: 'border-style="solid"',
          props: {
            icon: '$yzi-triangle',
            iconColor: 'yz-danger-500',
            borderStyle: 'solid',
            title: 'Secure area',
            description: 'This area is protected and secure.'
          },
          code: `yz-empty-state-card(
  icon="$yzi-triangle"
  icon-color="yz-danger-500"
  border-style="solid"
  title="Secure area"
  description="This area is protected and secure."
)`
        },
        {
          name: 'Action Styling',
          id: 'action-styling-example',
          description: 'Customizing action button appearance',
          label: 'Action Styling',
          hint: 'Custom action button',
          props: {
            icon: '$yzi-download-02',
            title: 'Download files',
            description: 'Click to download your files.',
            actionLabel: 'Download Now',
            actionSize: 'lg',
            actionColor: 'blue-500',
            actionTextColor: 'blue-100'
          },
          code: `yz-empty-state-card(
  icon="$yzi-download-02"
  title="Download files"
  description="Click to download your files."
  action-label="Download Now"
  action-size="lg"
  action-color="blue-500"
  action-text-color="blue-100"
  @action-click="handleDownload"
)`
        },
        {
          name: 'Custom Gap',
          id: 'custom-gap-example',
          description: 'Customizing spacing between elements',
          label: 'Custom Gap',
          hint: 'gap="20px"',
          props: {
            icon: '$yzi-package-plus',
            title: 'Spacing Example',
            description: 'This example shows custom gap spacing between elements.',
            actionLabel: 'Get Started',
            gap: '20px'
          },
          code: `yz-empty-state-card(
  icon="$yzi-package-plus"
  title="Spacing Example"
  description="This example shows custom gap spacing between elements."
  action-label="Get Started"
  gap="20px"
  @action-click="handleStart"
)`
        }
      ]
    },
    slotVariations() {
      return [
        {
          name: 'Custom Icon Slot',
          id: 'custom-icon-slot',
          description: 'Using the icon slot for custom content',
          label: 'Custom Icon Slot',
          hint: 'Custom icon content',
          isSlot: true,
          slotType: 'icon',
          props: {
            title: 'Custom Icon',
            description: 'This example uses a custom icon slot with multiple icons.'
          },
          code: `yz-empty-state-card(
  title="Custom Icon"
  description="This example uses a custom icon slot with multiple icons."
)
  template(#icon)
    .d-flex.align-center
      yz-icon(size="24" color="yz-neutral-500") $yzi-star-01
      yz-icon(size="24" color="yz-success-500") $yzi-check-circle
      yz-icon(size="24" color="yz-danger-500") $yzi-alert-circle`
        },
        {
          name: 'Custom Title Slot',
          id: 'custom-title-slot',
          description: 'Using the title slot for rich content',
          label: 'Custom Title Slot',
          hint: 'HTML in title',
          isSlot: true,
          slotType: 'title',
          props: {
            icon: '$yzi-box',
            description: 'This uses a custom title with styling.'
          },
          code: `yz-empty-state-card(
  icon="$yzi-box"
  description="This uses a custom title with styling."
)
  template(#title)
    .mb-4.text-center
      .yz-bold-body-sm.text-primary-500 Premium Feature
      .yz-regular-body-xs.text-neutral-400 Upgrade Required`
        },
        {
          name: 'Custom Action Slot',
          id: 'custom-action-slot',
          description: 'Using the action slot for multiple buttons',
          label: 'Custom Action Slot',
          hint: 'Multiple actions',
          isSlot: true,
          slotType: 'action',
          props: {
            icon: '$yzi-upload-01',
            title: 'Upload Files',
            description: 'Choose how you want to upload your files.'
          },
          code: `yz-empty-state-card(
  icon="$yzi-upload-01"
  title="Upload Files"
  description="Choose how you want to upload your files."
)
  template(#action)
    .d-flex.gap-2
      yz-btn(outlined size="sm" label="Browse Files")
      yz-btn(color="primary" size="sm" label="Drag & Drop")`
        }
      ]
    },
    componentProps() {
      return [
        {
          name: 'borderStyle',
          type: 'String',
          default: 'dashed',
          description: 'Border style for the card. Options: solid, dashed, dotted'
        },
        {
          name: 'gap',
          type: 'String | Number',
          default: '10px',
          description: 'Gap spacing between elements inside the card'
        },
        {
          name: 'icon',
          type: 'String',
          default: 'null',
          description: 'Icon name to display (e.g., $yzi-inbox)'
        },
        {
          name: 'iconSize',
          type: 'String | Number',
          default: '32px',
          description: 'Size of the icon'
        },
        {
          name: 'iconColor',
          type: 'String',
          default: 'yz-neutral-500',
          description: 'Color of the icon using design tokens or hex values'
        },
        {
          name: 'title',
          type: 'String',
          default: 'null',
          description: 'Title text to display'
        },
        {
          name: 'description',
          type: 'String',
          default: 'null',
          description: 'Description text to display below the title'
        },
        {
          name: 'actionLabel',
          type: 'String',
          default: 'null',
          description: 'Label for the action button'
        },
        {
          name: 'actionSize',
          type: 'String',
          default: 'sm',
          description: 'Size of the action button (sm, md, lg)'
        },
        {
          name: 'actionColor',
          type: 'String',
          default: 'primary',
          description: 'Color of the action button'
        }
      ]
    },
    componentSlots() {
      return [
        {
          name: 'icon',
          description: 'Custom content for the icon area. Overrides the icon prop.'
        },
        {
          name: 'title',
          description: 'Custom content for the title area. Overrides the title prop.'
        },
        {
          name: 'description',
          description: 'Custom content for the description area. Overrides the description prop.'
        },
        {
          name: 'action',
          description: 'Custom content for the action area. Overrides the actionLabel prop and allows multiple buttons.'
        }
      ]
    },
    componentEvents() {
      return [
        {
          name: 'action-click',
          parameters: 'none',
          description: 'Emitted when the action button is clicked (only when using actionLabel prop)'
        }
      ]
    },
    useCases() {
      return [
        {
          scenario: 'No search results',
          approach: 'Suggest alternative search terms or show popular items',
          example: '"No results found. Try different keywords or browse featured items."'
        },
        {
          scenario: 'Empty data table',
          approach: 'Explain why table is empty and provide action to populate it',
          example: '"No data available. Import your data or create your first entry."'
        },
        {
          scenario: 'New user onboarding',
          approach: 'Welcome user and guide them to their first action',
          example: '"Welcome! Create your first project to get started."'
        },
        {
          scenario: 'Feature not available',
          approach: 'Explain limitation and provide alternative or upgrade path',
          example: '"This feature requires a premium account. Upgrade to unlock."'
        },
        {
          scenario: 'Connection error',
          approach: 'Acknowledge the issue and provide recovery options',
          example: '"Connection lost. Check your internet and try again."'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    },
    handleActionClick(variationName) {
      console.log(`Action clicked for: ${variationName}`)
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}
</style>